import React, { useEffect, useState, useCallback } from 'react';
import deltaService from '../../services/deltaService';
import { Empty, PillGroup, ProvisionalNote, Section } from './DeltaUI';
import { Groups, HeadlineTiles, Matrix, PositionTable } from './NexusPresentation';

// Asset nexus — the central analytical view.
//
// Two questions decide what can move: how quickly a position converts to cash, and
// whether anything is pledged against it. A liquid asset that is encumbered is not
// available, and this screen must never imply otherwise.
//
// Every figure comes from the server already aggregated, and the rendering lives in
// NexusPresentation, shared with the read-only shareholder portal — so the two surfaces
// cannot show the same book differently.

const BOOKS = [
  { value: '', label: 'All books' },
  { value: 'A', label: 'Core' },
  { value: 'B', label: 'Income' },
  { value: 'C', label: 'Run-off' },
  { value: 'D', label: 'Retained' },
];

const TIERS = [
  { value: '', label: 'All tiers' },
  { value: 'C1', label: 'C1' },
  { value: 'C2', label: 'C2' },
  { value: 'C3', label: 'C3' },
  { value: 'C4', label: 'C4' },
  { value: 'R', label: 'Retained' },
];

const MODES = [
  { value: 'liq', label: 'Liquidity' },
  { value: 'enc', label: 'Encumbrance' },
  { value: 'mat', label: 'Both' },
];

export default function AssetNexusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [book, setBook] = useState('');
  const [tier, setTier] = useState('');
  const [mode, setMode] = useState('liq');

  const load = useCallback(() => {
    setLoading(true);
    deltaService.getNexus({ book: book || undefined, realisability_tier: tier || undefined })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [book, tier]);

  useEffect(() => { load(); }, [load]);

  const groups = mode === 'liq' ? data?.by_liquidity : data?.by_encumbrance;

  return (
    <div>
      {/* Names itself and says what the two questions are. Arriving here from a tile
          called "Dashboard", a reader should not have to infer from the filters what is
          being measured. */}
      <header className="mb-4">
        <h1 className="font-serif text-[26px] leading-tight tracking-tight text-gray-900 dark:text-gray-50 sm:text-[30px]">
          Dashboard
        </h1>
        {/* Full width, matching the tiles below — same reason as the Delta index intro. */}
        <p className="mt-1 text-[12px] leading-relaxed text-gray-500 dark:text-gray-400">
          The book as it stands. Two questions decide what can actually be used: how quickly
          a holding converts to cash, and whether it has been pledged as security for
          something else. A liquid asset that is pledged is not available.
        </p>
      </header>

      <ProvisionalNote completeness={data?.inventory_completeness} />
      <HeadlineTiles headline={data?.headline} />

      {/* Filters — horizontally scrollable pills on mobile, side by side from sm up. */}
      <div className="mb-4 grid gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-[#0d1117] sm:grid-cols-3 sm:p-4">
        <PillGroup label="Group by" options={MODES} value={mode} onChange={setMode} />
        <PillGroup label="Book" options={BOOKS} value={book} onChange={setBook} />
        <PillGroup label="Realisability" options={TIERS} value={tier} onChange={setTier} />
      </div>

      <Section
        title={mode === 'mat' ? 'Liquidity by encumbrance' : mode === 'liq' ? 'By liquidity' : 'By encumbrance'}
        subtitle="Amounts, not positions — a partly pledged holding splits between its free and pledged portions"
        onRerun={load}
        busy={loading}
      >
        {loading && !data ? <Empty>Loading…</Empty>
          : mode === 'mat' ? <Matrix matrix={data?.matrix} />
          : <Groups groups={groups} />}
      </Section>

      <Section
        title="Positions"
        subtitle={`${data?.positions?.length || 0} in the register${book || tier ? ' matching the filters' : ''}`}
        defaultOpen={false}
      >
        <PositionTable rows={data?.positions || []} />
      </Section>

      <p className="px-1 pb-2 text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">
        Realisability (C1–C4/R) and liquidity are different axes. Realisability is the exit
        route and horizon; liquidity is conversion speed. A position carries both.
      </p>
    </div>
  );
}
